import { Flag } from 'lucide-react'
import type { ReactNode } from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDayLabel } from '@/lib/dates'
import { cn } from '@/lib/utils'
import type { CallRequest, RequestFlag as Flagged, RequestHistoryEntry } from '@/types/clientMeetings'

const ordinal = (count: number) => {
  const teen = count % 100 > 10 && count % 100 < 14
  return `${count}${teen ? 'th' : (['th', 'st', 'nd', 'rd'][count % 10] ?? 'th')}`
}

function flagText(flag: Flagged): { label: string; className: string } {
  if (flag.kind === 'fixed') return { label: 'Already fixed', className: 'text-etn-success-text' }
  if (flag.kind === 'declined') return { label: 'Decided against', className: 'text-etn-text-label' }
  if (flag.kind === 'in_linear') return { label: 'Already in Linear', className: 'text-etn-info-text' }
  return { label: `Raised before (${ordinal(flag.count)} time)`, className: 'text-etn-danger-text' }
}

function outcomeText(entry: RequestHistoryEntry): string {
  if (entry.outcome === 'linked') return `linked to ${entry.ref ?? 'a ticket'}`
  if (entry.outcome === 'created') return `ticket created, ${entry.ref ?? ''}`.replace(/, $/, '')
  if (entry.outcome === 'dismissed') return entry.byName ? `dismissed by ${entry.byName}` : 'dismissed'
  return 'not logged'
}

/** The hover lines: every earlier raising with what happened to it, then the related requests. */
export function flagHover(request: CallRequest): string[] {
  const history = request.history.length ? `Also raised: ${request.history.map(entry => `${formatDayLabel(entry.date)} (${outcomeText(entry)})`).join(', ')}` : null
  const related = request.related.length ? `Related: ${request.related.map(item => `${formatDayLabel(item.date)}, ${item.title}`).join('; ')}` : null
  return [history, related].filter((line): line is string => Boolean(line))
}

const times = (count: number) => (count === 1 ? 'once' : count === 2 ? 'twice' : `${count} times`)

/** "not logged once, dismissed once": what happened to the earlier raisings, in the order first seen. */
function outcomesLine(history: RequestHistoryEntry[]): string {
  const kind = (entry: RequestHistoryEntry) => (entry.outcome === 'not_logged' ? 'not logged' : entry.outcome === 'dismissed' ? 'dismissed' : 'logged')
  const counts = history.reduce((tally, entry) => tally.set(kind(entry), (tally.get(kind(entry)) ?? 0) + 1), new Map<string, number>())
  return [...counts].map(([label, count]) => `${label} ${times(count)}`).join(', ')
}

function WithHover({ lines, children }: { lines: string[]; children: ReactNode }) {
  if (!lines.length) return <>{children}</>
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent sideOffset={6} className="max-w-sm space-y-1">
          {lines.map(line => (
            <p key={line}>{line}</p>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function RequestFlag({ request }: { request: CallRequest }) {
  const lines = flagHover(request)
  if (!request.flag) {
    if (!request.related.length) return null
    return (
      <WithHover lines={lines}>
        <span tabIndex={0} className="cursor-help border-b border-dotted border-current text-[12px] text-etn-text-soft">
          Related
        </span>
      </WithHover>
    )
  }
  const { label, className } = flagText(request.flag)
  return (
    <div>
      <WithHover lines={lines}>
        <span tabIndex={0} className={cn('inline-flex cursor-default items-center gap-1.5 whitespace-nowrap text-[12.5px] font-semibold', className)}>
          <Flag aria-hidden className="size-3.5 fill-current" />
          {label}
        </span>
      </WithHover>
      {request.flag.kind === 'raised_before' && request.history.length > 0 && (
        <div className="mt-0.5 text-[11.5px] text-etn-text-soft">{outcomesLine(request.history)}</div>
      )}
    </div>
  )
}
